import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { projectsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { ArrowLeft, Save, Calendar, Euro, Users, FileText, Target, Sparkles, AlertCircle } from 'lucide-react'

const ProjectEditPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    budget: '',
    deadline: '',
    requirements: '',
    industry: '',
    targetAudience: '',
    status: 'draft'
  })

  useEffect(() => {
    if (user?.role !== 'bedrijf' && user?.role !== 'agency') {
      navigate('/dashboard')
      return
    }
    fetchProject()
  }, [id, user])

  const fetchProject = async () => {
    try {
      setLoading(true)

      // First, try to get the project from user's own projects to verify ownership
      try {
        const myProjectsResponse = await projectsAPI.getMyProjects({ limit: 100 })
        const myProjects = myProjectsResponse.data.projects || []
        const isOwner = myProjects.some(p => p.id === id)

        if (!isOwner) {
          setError('Je hebt geen toegang tot dit project')
          setTimeout(() => navigate('/my-projects'), 2000)
          return
        }
      } catch (err) {
        console.error('Error checking ownership:', err)
      }

      // Now fetch the full project details
      const response = await projectsAPI.getProject(id)
      const project = response.data.project || response.data

      // Format deadline for date input
      const deadline = project.deadline ? new Date(project.deadline).toISOString().split('T')[0] : ''

      setFormData({
        title: project.title || '',
        description: project.description || '',
        budget: project.budget || '',
        deadline: deadline,
        requirements: project.requirements || '',
        industry: project.industry || '',
        targetAudience: project.targetAudience || '',
        status: project.status || 'draft'
      })
    } catch (error) {
      console.error('Error fetching project:', error)
      setError('Er is een fout opgetreden bij het ophalen van het project')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      // Prepare data for submission
      const updateData = {
        title: formData.title,
        description: formData.description,
        budget: formData.budget,
        deadline: formData.deadline,
        requirements: formData.requirements,
        targetAudience: formData.targetAudience,
        status: formData.status
      }

      // Only include industry if it has a value
      if (formData.industry && formData.industry.trim() !== '') {
        updateData.industry = formData.industry
      }

      // Don't send deliverables as a string - backend expects array
      // Skip deliverables field for now since it needs special handling

      await projectsAPI.updateProject(id, updateData)
      setSuccess('Project succesvol bijgewerkt!')
      setTimeout(() => navigate(`/projects/${id}`), 1500)
    } catch (error) {
      console.error('Error updating project:', error)
      setError(error.response?.data?.message || 'Er is een fout opgetreden bij het bijwerken')
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      // Prepare data for submission
      const updateData = {
        title: formData.title,
        description: formData.description,
        budget: formData.budget,
        deadline: formData.deadline,
        requirements: formData.requirements,
        targetAudience: formData.targetAudience,
        status: 'open'
      }

      // Only include industry if it has a value
      if (formData.industry && formData.industry.trim() !== '') {
        updateData.industry = formData.industry
      }

      await projectsAPI.updateProject(id, updateData)
      setSuccess('Project succesvol gepubliceerd!')
      setTimeout(() => navigate(`/projects/${id}`), 1500)
    } catch (error) {
      console.error('Error publishing project:', error)
      setError(error.response?.data?.message || 'Er is een fout opgetreden bij het publiceren')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="font-['Inter'] min-h-screen flex items-center justify-center bg-white text-[#0F172A]">
        <div className="w-8 h-8 border-2 border-[#1D8BF8] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="font-['Inter'] min-h-screen flex flex-col bg-white text-[#0F172A]">
      {/* Header */}
      <header className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 sticky top-0 z-50 bg-white/95 backdrop-blur-xl">
        <div className="flex items-center gap-6">
          <Link to="/" className="text-2xl font-black tracking-tighter text-[#0F172A] whitespace-nowrap">
            UGC<span className="text-[#1D8BF8]">4you</span>
          </Link>
        </div>

        <Link
          to="/my-projects"
          className="px-3 sm:px-4 py-2 min-h-[40px] bg-[#F8FAFC] hover:bg-gray-100 rounded-lg font-medium transition flex items-center gap-2 whitespace-nowrap"
        >
          <ArrowLeft className="w-4 h-4 flex-shrink-0" />
          <span className="sm:hidden">Terug</span>
          <span className="hidden sm:inline">Terug naar opdrachten</span>
        </Link>
      </header>

      {/* Main Content */}
      <div className="flex-1 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-4xl font-black mb-2">Project Bewerken</h1>
            <p className="text-[#475569] text-base sm:text-lg">Pas je UGC opdracht aan</p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                <p className="text-sm text-red-500">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-emerald-50 border border-green-200 rounded-xl">
              <p className="text-sm text-emerald-600">{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Project Titel <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Bijv. Instagram Reels voor productlancering"
                className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl focus:border-[#1D8BF8] focus:outline-none transition"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Beschrijving <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Beschrijf je project en wat je zoekt..."
                rows={6}
                className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl focus:border-[#1D8BF8] focus:outline-none transition resize-none"
                required
              />
            </div>

            {/* Budget & Deadline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Euro className="inline w-4 h-4 mr-1" />
                  Budget <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  placeholder="1000"
                  min="0"
                  className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl focus:border-[#1D8BF8] focus:outline-none transition"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Calendar className="inline w-4 h-4 mr-1" />
                  Deadline
                </label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                  className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl focus:border-[#1D8BF8] focus:outline-none transition"
                />
              </div>
            </div>

            {/* Requirements */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <FileText className="inline w-4 h-4 mr-1" />
                Vereisten
              </label>
              <textarea
                value={formData.requirements}
                onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                placeholder="Wat zijn de specifieke vereisten voor creators?"
                rows={4}
                className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl focus:border-[#1D8BF8] focus:outline-none transition resize-none"
              />
            </div>


            {/* Industry & Target Audience */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Industrie
                </label>
                <input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  placeholder="Bijv. Fashion, Tech, Food"
                  className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl focus:border-[#1D8BF8] focus:outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Target className="inline w-4 h-4 mr-1" />
                  Doelgroep
                </label>
                <input
                  type="text"
                  value={formData.targetAudience}
                  onChange={(e) => setFormData({...formData, targetAudience: e.target.value})}
                  placeholder="Bijv. 18-25 jaar, vrouwen"
                  className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl focus:border-[#1D8BF8] focus:outline-none transition"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-[#3EB489] text-white rounded-xl font-medium hover:bg-[#359E78] hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Opslaan...' : 'Wijzigingen Opslaan'}
              </button>

              {formData.status === 'draft' && (
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-xl font-medium transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Sparkles className="w-5 h-5" />
                  Publiceren
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ProjectEditPage